import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Hash, Loader2, RefreshCw, UserRoundSearch } from 'lucide-react';
import {
    fetchForeignAccessLog,
    FOREIGN_LOG_DAYS,
    ForeignAccessGrant,
    isForeignGrantActive,
} from '../../services/foreignAccess';

/**
 * Fremdzugriffe der letzten Tage — der Sammelblick der Verwaltung
 * (SQL Nr. 128, docs/mitarbeiter-cockpit-plan.md).
 *
 * Eigentuemer und Verantwortliche erfahren von einem Fremdzugriff ueber ihre
 * Mitteilungen; die Verwaltung sieht hier ALLE auf einmal, mit Begruendung.
 * Wer welche Zeilen sieht, entscheidet die RLS (Admin: eigene Firma,
 * Super-Admin: alles) — die Liste filtert nichts nach.
 *
 * Steht unter den Meldungen, weil es derselbe Blick ist: etwas, das jemand in
 * der Verwaltung wissen sollte, ohne dass eine Handlung faellig waere.
 */

/** Was die Liste von einer Projektzeile braucht — passt auf jede Zeile aus `projects`. */
interface ProjectRef {
    id: string;
    name?: string | null;
    project_number?: string | null;
    data?: { metadata?: { projectNumber?: string | null; projectName?: string | null } | null } | null;
}

interface Props {
    /** Zum Aufloesen von Nummer und Name; fehlt ein Projekt, steht seine Kennung da. */
    projects: ReadonlyArray<ProjectRef>;
    onOpenProject?: (project: ProjectRef) => void;
}

const zeitpunkt = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * Ende der Freigabe. Der Server setzt Mitternacht des Folgetages — „bis
 * 26.09., 00:00" liest sich aber wie ein Tag zu viel. Mitternacht steht
 * deshalb als 24:00 des Freigabetages da, so wie es der Dialog verspricht.
 */
const gueltigBis = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    if (d.getHours() === 0 && d.getMinutes() === 0) {
        const vortag = new Date(d.getTime() - 60_000);
        return `${vortag.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}, 24:00`;
    }
    return zeitpunkt(iso);
};

export const AdminForeignAccessList: React.FC<Props> = ({ projects, onOpenProject }) => {
    const [rows, setRows] = useState<ForeignAccessGrant[]>([]);
    const [missing, setMissing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [reloadTick, setReloadTick] = useState(0);
    /**
     * Zeitpunkt des Ladens — Bezug fuer „aktiv". Bewusst nicht die laufende
     * Uhr: die Liste ist eine Momentaufnahme, „Aktualisieren" stellt beide neu.
     */
    const [loadedAtMs, setLoadedAtMs] = useState(0);

    useEffect(() => {
        let abgebrochen = false;
        void fetchForeignAccessLog().then((log) => {
            if (abgebrochen) return;
            setRows(log.rows);
            setLoadedAtMs(Date.now());
            setMissing(log.missing);
            setError(log.error);
            setLoading(false);
        });
        return () => { abgebrochen = true; };
    }, [reloadTick]);

    // `loading` setzt der Klick, nicht der Effekt — der Anfangswert ist schon true.
    const reload = useCallback(() => {
        setLoading(true);
        setReloadTick((t) => t + 1);
    }, []);

    const projectById = useMemo(() => {
        const map = new Map<string, ProjectRef>();
        for (const p of projects || []) if (p?.id) map.set(p.id, p);
        return map;
    }, [projects]);

    return (
        <section className="mt-8 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="bg-amber-50 p-1.5 rounded-lg border border-amber-200 shrink-0">
                        <UserRoundSearch className="w-5 h-5 text-amber-700" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="font-semibold text-slate-800 leading-tight">
                            Fremdzugriffe{!loading && !missing ? ` (${rows.length})` : ''}
                        </h3>
                        <p className="text-[11px] text-slate-500">
                            Letzte {FOREIGN_LOG_DAYS} Tage — Arbeit in Projekten ohne eigene Zuständigkeit, mit Begründung.
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={reload}
                    disabled={loading}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    Aktualisieren
                </button>
            </div>

            {missing ? (
                <p className="m-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    SQL Nr. 128 fehlt auf dieser Umgebung — Fremdzugriffe werden noch nicht erfasst.
                </p>
            ) : error ? (
                <p className="m-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
            ) : loading && rows.length === 0 ? (
                <p className="px-4 py-6 text-xs text-slate-500 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Fremdzugriffe werden geladen …
                </p>
            ) : rows.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-slate-500">
                    Keine Fremdzugriffe in den letzten {FOREIGN_LOG_DAYS} Tagen.
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                            <tr>
                                <th className="text-left font-semibold px-4 py-2 whitespace-nowrap">Zeit</th>
                                <th className="text-left font-semibold px-4 py-2 whitespace-nowrap">Mitarbeiter</th>
                                <th className="text-left font-semibold px-4 py-2 whitespace-nowrap">Projekt</th>
                                <th className="text-left font-semibold px-4 py-2 min-w-[16rem]">Begründung</th>
                                <th className="text-left font-semibold px-4 py-2 whitespace-nowrap">Gültig bis</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((g) => {
                                const p = projectById.get(g.projectId);
                                const nummer = p?.project_number || p?.data?.metadata?.projectNumber || '';
                                const name = p?.name || p?.data?.metadata?.projectName || '';
                                const aktiv = isForeignGrantActive(g, loadedAtMs);
                                return (
                                    <tr key={g.id} className="align-top">
                                        <td className="px-4 py-2.5 whitespace-nowrap text-slate-600 tabular-nums">{zeitpunkt(g.createdAt)}</td>
                                        <td className="px-4 py-2.5 whitespace-nowrap font-semibold text-slate-700">{g.userName || 'Unbekannt'}</td>
                                        <td className="px-4 py-2.5">
                                            {p ? (
                                                <button
                                                    type="button"
                                                    onClick={onOpenProject ? () => onOpenProject(p) : undefined}
                                                    disabled={!onOpenProject}
                                                    className="text-left disabled:cursor-default group"
                                                >
                                                    <span className={`block font-semibold text-slate-800 ${onOpenProject ? 'group-hover:text-blue-700 group-hover:underline' : ''}`}>{name || 'Ohne Namen'}</span>
                                                    {nummer && (
                                                        <span className="flex items-center gap-1 text-[11px] text-slate-500">
                                                            <Hash className="w-3 h-3" />{nummer}
                                                        </span>
                                                    )}
                                                </button>
                                            ) : (
                                                <span className="text-[11px] text-slate-500" title={g.projectId}>
                                                    Nicht in der Projektliste
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-700 whitespace-pre-line break-words">{g.reason}</td>
                                        <td className="px-4 py-2.5 whitespace-nowrap tabular-nums">
                                            <span className={aktiv ? 'text-emerald-700 font-semibold' : 'text-slate-500'}>{gueltigBis(g.validUntil)}</span>
                                            {aktiv && <span className="ml-1.5 text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-semibold">aktiv</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};
